import { useNavigate } from 'react-router-dom';
import { StudyTable } from '@/features/studies';
import { useGetStudiesListQuery } from '@/state/slices/studies';
import { usePageLoading } from '@/hooks/usePageLoading';
import { LayoutSubSidebar, LayoutPageTitle } from '@/features/layout';
import { useGetTagClassOptionsQuery } from '@/state/slices/options';
import { EmptyState } from '@/shared/ui/EmptyState';
import { studyDateKey } from '@/shared/utils/studyDate';
import { RecentlyViewed } from '@/features/reader-empty-state';
import { useGetViewsQuery } from '@/state/slices/views';
import { WorklistFilterRail, useWorklistFilters } from '@/features/worklist';
import pageStyles from './Worklist.module.css';

const Worklist = () => {
  const navigate = useNavigate();

  const { data: tagClasses } = useGetTagClassOptionsQuery();

  const { data: viewsData } = useGetViewsQuery({
    page_size: 3,
    object_type: 'STUDY',
  });
  const recentlyViewed = viewsData?.results ?? [];

  const {
    params,
    page,
    pageSize,
    setPage,
    setPageSize,
    assignedToMe,
    setAssignedToMe,
    reportStatusFilters,
    setReportStatusFilters,
    priorityFilters,
    setPriorityFilters,
    tagFilters,
    setTagFilters,
    searchText,
    setSearchText,
    setOrdering,
    hasActiveFilters,
    clearFilters,
  } = useWorklistFilters();

  const { data, isFetching, isLoading } = useGetStudiesListQuery(params);
  const studies = data?.results ?? [];
  const totalCount = data?.count ?? 0;

  usePageLoading(isLoading);

  const exportCsv = () => {
    const head = [
      'Patient First',
      'Patient Last',
      'MRN',
      'Study Type',
      'Priority',
      'Report Status',
      'Site',
      'Study Date',
    ];
    const rows = studies.map((s) => [
      s.patientFirstName,
      s.patientLastName,
      s.patientMedicalRecordNumber,
      s.modality,
      s.priority,
      s.reportStatus,
      s.site,
      studyDateKey(s.studyDate),
    ]);
    const csv = [head, ...rows]
      .map((r) =>
        r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')
      )
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'studies.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const isEmpty = !isFetching && studies.length === 0;

  const renderEmptyState = () => {
    const recents =
      recentlyViewed.length > 0 ? (
        <RecentlyViewed studies={recentlyViewed} />
      ) : null;

    if (hasActiveFilters) {
      return (
        <EmptyState
          headline="Nothing matching your filters"
          subtext="Try clearing or adjusting your filters to see studies."
          action={{ label: 'Clear filters', onClick: clearFilters }}
        >
          {recents}
        </EmptyState>
      );
    }

    return (
      <EmptyState
        headline="You're all caught up"
        subtext="New studies will show up as soon as they're available."
      >
        {recents}
      </EmptyState>
    );
  };

  return (
    <>
      <LayoutPageTitle title="Worklist" />
      <LayoutSubSidebar title="Filters">
        <WorklistFilterRail
          assignedToMe={assignedToMe}
          onAssignedChange={setAssignedToMe}
          reportStatusFilters={reportStatusFilters}
          onReportStatusChange={setReportStatusFilters}
          priorityFilters={priorityFilters}
          onPriorityChange={setPriorityFilters}
          tagFilters={tagFilters}
          onTagChange={setTagFilters}
          tags={tagClasses}
          searchText={searchText}
          onSearchChange={setSearchText}
          onExportCsv={exportCsv}
        />
      </LayoutSubSidebar>

      {isEmpty ? (
        renderEmptyState()
      ) : (
        <div className={pageStyles.tableContainer}>
          <StudyTable
            studies={studies}
            loading={isFetching}
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            onSortChange={setOrdering}
            onStudyClick={(uuid) => navigate(`/studies/${uuid}`)}
          />
        </div>
      )}
    </>
  );
};

export default Worklist;
